/**
 * Purpose: General utility functions shared across the application.
 * Owns: ID generation, array helpers, formatting utilities.
 * Notes: Keep this file focused on pure functions with no side effects.
 *        Domain-specific logic belongs in the engine/ modules, not here.
 */

/**
 * Generate a simple unique ID with an optional prefix.
 * Not cryptographically secure — fine for local game state.
 */
export function generateId(prefix = 'id'): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}-${timestamp}-${random}`;
}

/**
 * Shuffle an array using Fisher-Yates algorithm.
 * Returns a new array — does not mutate the original.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Clamp a number between a minimum and maximum value.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
